import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatDistanceToNowStrict, format } from 'date-fns';

/** Merge Tailwind classes safely (handles conflicts via tailwind-merge). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Truncate a ULID to a short, recognizable id (last 8 chars). */
export function shortId(id: string): string {
  return id.slice(-8);
}

/** "5 min ago" relative time. */
export function relativeTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  try {
    return formatDistanceToNowStrict(new Date(date), { addSuffix: true });
  } catch {
    return '—';
  }
}

/** Absolute time for tooltips and audit. */
export function absoluteTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  try {
    return format(new Date(date), 'yyyy-MM-dd HH:mm:ss');
  } catch {
    return '—';
  }
}

/** Format role names (e.g. TECHNICIAN → Technician). */
export function roleLabel(role: string): string {
  return role.charAt(0) + role.slice(1).toLowerCase();
}

/** Format bytes for session stats. */
export function formatBytes(bytes: number | string | bigint): string {
  const n = typeof bytes === 'bigint' ? Number(bytes) : Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
